import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { APIErrorResponseGenerator } from '@/lib/helper';
import handleGA4Event, { eventTracker } from '@/lib/service-workers';

import { showToast } from '@/components/@base/toast/show-toast';
import { usePostAddToCart } from '@/components/app/cart/services/hooks';
import { useGetProfile } from '@/components/layout/services/hooks';

import { useSmartOfferStore } from '@/store/smart-offer.store';

import { pageLevelLocalization } from '@/constant/localization';
import { GET_CART } from '@/constant/query-key';
import SmartOfferModel from '@/models/smart-offer.model';

const { cart: cartLocalization } = pageLevelLocalization;

interface UseAddToCartHandlerOptions {
  onSuccess?: () => void; // Optional callback for success
}

function useAddToCartHandler(options?: UseAddToCartHandlerOptions) {
  const queryClient = useQueryClient();
  const { profile } = useGetProfile();
  const { mutate, isPending } = usePostAddToCart();
  const { setShowSmartOffer } = useSmartOfferStore();

  const handleAddToCart = ({ data }: { data: SmartOfferModel }) => {
    let slug: string[] | undefined;

    if (data.productType === 'addon') {
      slug = [data.product.slug].filter(Boolean);
    }

    const cartData =
      data.productType === 'addon'
        ? {
            id: data.product.targetId,
            smart_offer: data.id,
            slug,
          }
        : {
            id: data.productId,
            smart_offer: data.id,
          };

    if (!data) return;
    mutate(cartData, {
      onError: (e) => {
        if (e instanceof AxiosError) showToast(APIErrorResponseGenerator(e));
      },
      onSuccess: (res) => {
        if (res.appliedCodeSteppedDiscount) {
          showToast({ message: res.appliedCodeSteppedDiscount, type: 'default' });
        }
        queryClient.setQueryData([GET_CART], res);
        eventTracker(profile?.mobile, 'add_to_basket', new Date().toISOString(), {
          productId: data.productId,
        });
        const product = res?.products?.find((item) => item?.id === data.productId);
        handleGA4Event(
          'add_to_cart',
          [
            {
              item_id: data.productId,
              item_name: product?.title,
              price: product?.price,
              index: 0,
              quantity: 1,
              affiliation: product?.store?.slug,
              discount: product?.discount,
            },
          ],
          {
            currency: 'USD',
            value: res?.total,
          },
        );
        setShowSmartOffer(false);
        showToast({ message: cartLocalization.productSuccessfullyAdded, type: 'success' });
        options?.onSuccess?.(); // Call the optional success callback
      },
    });
  };

  return { handleAddToCart, isAddingToCart: isPending };
}

export default useAddToCartHandler;
